import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";

// Create user registration thunk
export const registerUser = createAsyncThunk(
  "createAccount/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/users/`,
        {
          method: "POST",
          body: JSON.stringify(userData),
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error || "Registration failed. Please try again.");
    }
  }
);

// Add this new thunk after the registerUser thunk
export const requestPasswordReset = createAsyncThunk(
  "createAccount/requestPasswordReset",
  async (userEmail, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/cognito/forgot-password/`,
        {
          method: "POST",
          body: JSON.stringify(userEmail),
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Password reset request failed. Please try again."
      );
    }
  }
);

// Add this new thunk after the requestPasswordReset thunk
export const confirmPasswordReset = createAsyncThunk(
  "createAccount/confirmPasswordReset",
  async (resetData, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/cognito/confirm-forgot-password/`,
        {
          method: "POST",
          body: JSON.stringify(resetData),
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Password reset failed. Please try again."
      );
    }
  }
);

// Add new thunk for authenticated user password reset
export const resetUserPassword = createAsyncThunk(
  "createAccount/resetUserPassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/users/set_password/`,
        {
          method: "POST",
          body: JSON.stringify({
            new_password: passwordData.new_password,
            current_password: passwordData.current_password,
          }),
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Password reset failed. Please try again."
      );
    }
  }
);

// Add new thunk for submitting background survey
export const submitBackgroundSurvey = createAsyncThunk(
  "createAccount/submitBackgroundSurvey",
  async (surveyData, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/user/backgrounds/`,
        {
          method: "POST",
          body: JSON.stringify(surveyData),
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to submit background survey. Please try again."
      );
    }
  }
);

const initialState = {
  isLoading: false,
  createAccountError: null,
  success: false,
  userEmail: null,
  isResetLoading: false,
  resetPasswordError: null,
  resetPasswordSuccess: false,
  isUserPasswordResetLoading: false,
  userPasswordResetError: null,
  userPasswordResetSuccess: false,
  isBackgroundSurveyLoading: false,
  backgroundSurveyError: null,
  backgroundSurveySuccess: false,
  isConfirmPasswordResetLoading: false,
  confirmPasswordResetError: null,
  confirmPasswordResetSuccess: false,
};

const createAccountSlice = createSlice({
  name: "createAccount",
  initialState,
  reducers: {
    clearError: (state) => {
      state.createAccountError = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    clearResetPasswordError: (state) => {
      state.resetPasswordError = null;
      state.isResetLoading = false;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
      state.resetPasswordError = null;
      state.isResetLoading = false;
    },
    clearConfirmPasswordResetError: (state) => {
      state.confirmPasswordResetError = null;
      state.isConfirmPasswordResetLoading = false;
    },
    clearConfirmPasswordResetSuccess: (state) => {
      state.confirmPasswordResetSuccess = false;
      state.confirmPasswordResetError = null;
      state.isConfirmPasswordResetLoading = false;
    },
    clearUserPasswordResetError: (state) => {
      state.userPasswordResetError = null;
      state.isUserPasswordResetLoading = false;
    },
    clearUserPasswordResetSuccess: (state) => {
      state.userPasswordResetSuccess = false;
      state.userPasswordResetError = null;
      state.isUserPasswordResetLoading = false;
    },
    clearBackgroundSurveyError: (state) => {
      state.backgroundSurveyError = null;
    },
    clearBackgroundSurveySuccess: (state) => {
      state.backgroundSurveySuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
        state.userEmail = action.payload.email;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.createAccountError = action.payload;
        // Check if the error is due to an email that is already linked to an account
        if (action.payload.email) {
          state.createAccountError = "Email already linked to an account.";
        }
      })
      .addCase(requestPasswordReset.pending, (state) => {
        state.isResetLoading = true;
        state.resetPasswordError = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state) => {
        state.isResetLoading = false;
        state.resetPasswordSuccess = true;
        state.resetPasswordError = null;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.isResetLoading = false;
        state.resetPasswordError = action.payload;
      })
      .addCase(confirmPasswordReset.pending, (state) => {
        state.isConfirmPasswordResetLoading = true;
        state.confirmPasswordResetError = null;
      })
      .addCase(confirmPasswordReset.fulfilled, (state) => {
        state.isConfirmPasswordResetLoading = false;
        state.confirmPasswordResetSuccess = true;
        state.confirmPasswordResetError = null;
      })
      .addCase(confirmPasswordReset.rejected, (state, action) => {
        state.isConfirmPasswordResetLoading = false;

        if (action.payload?.error === "Invalid confirmation code") {
          state.confirmPasswordResetError = "Incorrect Code";
        } else {
          state.confirmPasswordResetError = action.payload?.error;
        }
      })
      .addCase(resetUserPassword.pending, (state) => {
        state.isUserPasswordResetLoading = true;
        state.userPasswordResetError = null;
      })
      .addCase(resetUserPassword.fulfilled, (state) => {
        state.isUserPasswordResetLoading = false;
        state.userPasswordResetSuccess = true;
        state.userPasswordResetError = null;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isUserPasswordResetLoading = false;
        state.userPasswordResetError = action.payload;
      })
      .addCase(submitBackgroundSurvey.pending, (state) => {
        state.isBackgroundSurveyLoading = true;
        state.backgroundSurveyError = null;
      })
      .addCase(submitBackgroundSurvey.fulfilled, (state) => {
        state.isBackgroundSurveyLoading = false;
        state.backgroundSurveySuccess = true;
        state.backgroundSurveyError = null;
      })
      .addCase(submitBackgroundSurvey.rejected, (state, action) => {
        state.isBackgroundSurveyLoading = false;
        state.backgroundSurveyError = action.payload;
      });
  },
});

export const {
  clearError,
  resetSuccess,
  clearResetPasswordError,
  clearResetPasswordSuccess,
  clearConfirmPasswordResetError,
  clearConfirmPasswordResetSuccess,
  clearUserPasswordResetError,
  clearUserPasswordResetSuccess,
  clearBackgroundSurveyError,
  clearBackgroundSurveySuccess,
} = createAccountSlice.actions;
export default createAccountSlice.reducer;
